import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import FormulaExplainer from "@/pages/FormulaExplainer";
import MetadataCleaner from "@/pages/MetadataCleaner";
import TrackerGenerator from "@/pages/TrackerGenerator";
import LeaveGenerator from "@/pages/LeaveGenerator";
import JsonToExcel from "@/pages/JsonToExcel";
import CaseFixer from "@/pages/CaseFixer";
import ExcuseGenerator from "@/pages/ExcuseGenerator";
import QRGenerator from "@/pages/QRGenerator";
import InvoiceGenerator from "@/pages/InvoiceGenerator";
import PdfToExcel from "@/pages/PdfToExcel";
import PhpToExcel from "@/pages/PhpToExcel";
import ImageCompressor from "@/pages/ImageCompressor";
import JsonFormatter from "@/pages/JsonFormatter";
import DocxToPdf from "@/pages/DocxToPdf";
import PdfMerger from "@/pages/PdfMerger";

function SEO({ title, description, keywords, canonical }) {
  const url = canonical || window.location.href;
  
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": title,
          "description": description,
          "url": url,
          "applicationCategory": "UtilitiesApplication",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "operatingSystem": "Any"
        })}
      </script>
    </Helmet>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<><SEO title="ToolPaste - Free Productivity Tools for Teams | Formula Explainer, Tracker, Case Fixer" description="7 free productivity tools: generate tracker sheets, explain Excel formulas, fix text casing, convert JSON to Excel, generate leave applications, and more. No sign-up." /><Home /></>} />
            <Route path="/tracker-generator" element={<><SEO title="Requirements Tracker Sheet Generator - Checklist to Excel | ToolPaste" description="Paste your requirements or checklist and get a structured, color-coded Excel tracker with categories, priorities, and status columns. Free tool." /><TrackerGenerator /></>} />
            <Route path="/formula-explainer" element={<><SEO title="Excel Formula Explainer - Explain Any Formula in Plain English | ToolPaste" description="Paste any Excel or Google Sheets formula and get a clear, step-by-step explanation in simple English. Free, no sign-up needed." /><FormulaExplainer /></>} />
            <Route path="/case-fixer" element={<><SEO title="Smart Case Fixer - Fix Text Casing with AI | ToolPaste" description="Fix messy text casing intelligently. Handles proper nouns, acronyms, and brand names. Free AI-powered case fixer tool." /><CaseFixer /></>} />
            <Route path="/json-to-excel" element={<><SEO title="JSON to Excel Converter - Convert JSON to XLSX Online | ToolPaste" description="Convert JSON data to formatted Excel spreadsheet instantly. Free online JSON to Excel converter with styled headers and auto-filters." /><JsonToExcel /></>} />
            <Route path="/leave-generator" element={<><SEO title="Leave Application Generator - Professional Leave Emails | ToolPaste" description="Generate professional leave application emails in seconds. Supports sick leave, vacation, personal leave, and more. Free tool." /><LeaveGenerator /></>} />
            <Route path="/excuse-generator" element={<><SEO title="Meeting Excuse Generator - Polite Professional Excuses | ToolPaste" description="Generate polite, professional excuses to skip meetings. Copy and send in one click. No one will know." /><ExcuseGenerator /></>} />
            <Route path="/metadata-cleaner" element={<><SEO title="File Metadata Cleaner - Remove Hidden Data from Files | ToolPaste" description="Remove hidden metadata like GPS location, author info, and edit history from images, PDFs, and documents. Protect your privacy." /><MetadataCleaner /></>} />
            <Route path="/qr-generator" element={<><SEO title="QR Code Generator - Create Custom QR Codes Free | ToolPaste" description="Generate QR codes for URLs, WiFi, contact info, and more. Customize colors and size. Download instantly." /><QRGenerator /></>} />
            <Route path="/invoice-generator" element={<><SEO title="Invoice Generator - Create Professional Invoices Free | ToolPaste" description="Generate professional PDF invoices for freelancers and small businesses. No sign-up required." /><InvoiceGenerator /></>} />
            <Route path="/pdf-to-excel" element={<><SEO title="PDF to Excel Converter - Extract Tables from PDF | ToolPaste" description="Convert PDF files to Excel spreadsheets. Automatically extracts tables and text data." /><PdfToExcel /></>} />
            <Route path="/php-to-excel" element={<><SEO title="PHP Unserialize to Excel - Recursive Unserialization Tool | ToolPaste" description="The only tool with recursive PHP unserialization. Convert nested PHP serialized data to Excel with JSON preview. One of a kind." /><PhpToExcel /></>} />
            <Route path="/image-compressor" element={<><SEO title="Image Compressor - Reduce Image Size Online Free | ToolPaste" description="Compress JPG, PNG, and WebP images while maintaining quality. Reduce file size up to 80%." /><ImageCompressor /></>} />
            <Route path="/json-formatter" element={<><SEO title="JSON Formatter & Validator - Beautify JSON Online | ToolPaste" description="Format, validate, and beautify JSON data. View structure statistics and copy formatted output." /><JsonFormatter /></>} />
            <Route path="/docx-to-pdf" element={<><SEO title="DOCX to PDF Converter - Convert Word to PDF Free | ToolPaste" description="Convert Microsoft Word documents to PDF format instantly. Fast and secure conversion." /><DocxToPdf /></>} />
            <Route path="/pdf-merger" element={<><SEO title="PDF Merger - Combine Multiple PDFs Online Free | ToolPaste" description="Merge multiple PDF files into one document. Reorder pages and combine PDFs easily." /><PdfMerger /></>} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
